import { $page } from '../../../../shared/constants/page';
import { $pageWithComponents } from '../../../../shared/constants/scan';
import { manualAuditStatus } from '../../../../shared/constants/scanning';
import { AiScanStatus } from '../../../../shared/interfaces/manual-audit.interface';
import { IAiTestingPageResult, IPageWithComponents } from '../../../../shared/interfaces/scan.interface';

export enum AiTestingPageDisplayStatus {
  completed = 'completed',
  failed = 'failed',
  processing = 'processing',
}

export interface IAiTestingPageProgressRow {
  pageId: string;
  label: string;
  status: AiTestingPageDisplayStatus;
  error?: string;
}

export interface IAiTestingProgressSummary {
  completed: number;
  processing: number;
  failed: number;
}

export interface IAiTestingProgress {
  rows: IAiTestingPageProgressRow[];
  summary: IAiTestingProgressSummary;
}

export interface IAiTestingProgressPanelInputs {
  workspaceId: string;
  digitalPropertyId: string;
  scanId: string;
  useAdminApi: boolean;
}

/**
 * Derives per-page progress rows from the evaluation scope and the stored page results.
 * Rows are the scope pages that have a URL, in scope order. A page without a result is processing only while the
 * evaluation itself is processing; otherwise it is omitted. Results for pages outside the scope are ignored.
 */
export function buildAiTestingProgress(
  scopePages: IPageWithComponents[] | undefined,
  results: IAiTestingPageResult[] | undefined,
  evaluationStatus: manualAuditStatus | undefined,
): IAiTestingProgress {
  const resultsByPageId: Map<string, IAiTestingPageResult> = new Map(
    (results ?? []).map((result: IAiTestingPageResult): [string, IAiTestingPageResult] => [result.pageId, result]),
  );
  const isEvaluationProcessing: boolean = evaluationStatus === manualAuditStatus.processing;

  const rows: IAiTestingPageProgressRow[] = [];
  for (const scopePage of scopePages ?? []) {
    const url: string | undefined = scopePage[$pageWithComponents.page]?.[$page.url];
    if (!url) {
      continue;
    }

    const pageId: string = scopePage[$pageWithComponents.pageId];
    const name: string | undefined = scopePage[$pageWithComponents.page]?.[$page.name];
    const label: string = name?.trim() ? name : url;
    const result: IAiTestingPageResult | undefined = resultsByPageId.get(pageId);

    if (!result) {
      if (isEvaluationProcessing) {
        rows.push({ pageId, label, status: AiTestingPageDisplayStatus.processing });
      }
      continue;
    }

    if (result.status === AiScanStatus.failed) {
      rows.push({ pageId, label, status: AiTestingPageDisplayStatus.failed, error: result.error });
    } else {
      rows.push({ pageId, label, status: AiTestingPageDisplayStatus.completed });
    }
  }

  const summary: IAiTestingProgressSummary = { completed: 0, processing: 0, failed: 0 };
  for (const row of rows) {
    summary[row.status] += 1;
  }

  return { rows, summary };
}
